import { useEffect, useState, useCallback } from "react";
import { CheckCircle, XCircle, Clock, ArrowsClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

// Same order as the Ship platform picker so the operator's mental model
// is consistent across the app.
const PLATFORMS = ["Facebook", "Instagram", "LinkedIn", "Threads", "TikTok", "YouTube", "Twitter"];

/**
 * At-a-glance panel showing which Buffer channels are configured,
 * which are in a rate-limit cooldown, and which are unconfigured.
 *
 * Reads the same two endpoints as the Ship platform dialog so both
 * views can never disagree:
 *   GET /content-studio/configured-platforms  → { configured, missing }
 *   GET /content-studio/rate-limit-cooldowns  → { cooldowns }
 *
 * `onDetect`: optional callback the parent supplies. If passed, the
 * "Detect from Buffer" button here calls it (so the Settings page can
 * reuse its existing detect handler that also persists channel IDs).
 * Otherwise this component runs its own detect that just refreshes the
 * status display.
 *
 * `compact`: when true (Dashboard tile), collapses the description +
 * shrinks vertical rhythm. Default false = full Settings-style panel.
 */
export default function BufferChannelsPanel({ onDetect, compact = false, title = "Buffer Channels" }) {
    const [configured, setConfigured] = useState({});
    const [missing, setMissing] = useState([]);
    const [cooldowns, setCooldowns] = useState({});
    const [loading, setLoading] = useState(true);
    const [detecting, setDetecting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [cfg, cd] = await Promise.all([
                api.get("/content-studio/configured-platforms").catch(() => ({ data: {} })),
                api.get("/content-studio/rate-limit-cooldowns").catch(() => ({ data: {} })),
            ]);
            setConfigured(cfg.data?.configured || {});
            setMissing(cfg.data?.missing || []);
            setCooldowns(cd.data?.cooldowns || {});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const handleDetect = async () => {
        setDetecting(true);
        try {
            if (onDetect) {
                await onDetect();
            } else {
                const r = await api.post("/content-studio/settings/discover-buffer-channels", { apply: true });
                const d = r.data || {};
                if (!d.ok) {
                    toast.error(d.message || "Detect failed");
                    return;
                }
                const keys = Object.keys(d.applied || {});
                toast.success(
                    keys.length
                        ? `Synced ${keys.length} channel${keys.length === 1 ? "" : "s"} from Buffer`
                        : "Channels already up-to-date",
                );
            }
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Detect failed");
        } finally {
            setDetecting(false);
        }
    };

    const readyCount = Object.keys(configured).length;
    const missingCount = missing.length;
    const cooldownCount = Object.keys(cooldowns).length;

    return (
        <div
            data-testid="buffer-channels-panel"
            className={[
                "border border-border rounded-sm bg-card",
                compact ? "p-3" : "p-4",
            ].join(" ")}
        >
            <div className={compact ? "flex items-center justify-between mb-2" : "flex items-center justify-between mb-3"}>
                <div>
                    <h3 className="text-sm font-semibold tracking-tight uppercase font-mono">{title}</h3>
                    {!compact && (
                        <p className="text-[11px] font-mono text-muted-foreground mt-1">
                            Which networks Ship-to-Buffer can publish to right now.
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div
                        className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
                        data-testid="buffer-channels-summary"
                    >
                        <span className="text-emerald-700">{readyCount} ready</span>
                        {cooldownCount > 0 && <> · <span className="text-amber-700">{cooldownCount} cooldown</span></>}
                        {missingCount > 0 && <> · <span className="text-rose-700">{missingCount} not set</span></>}
                    </div>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 rounded-sm"
                        onClick={refresh}
                        disabled={loading}
                        data-testid="buffer-channels-refresh"
                        title="Refresh channel status"
                    >
                        <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
                    </Button>
                </div>
            </div>

            <div className="space-y-1">
                {PLATFORMS.map((p) => {
                    const channelId = configured[p];
                    const cooldownIso = cooldowns[p.toLowerCase()];
                    const inCooldown = Boolean(cooldownIso);
                    const notConfigured = missing.includes(p);
                    const cooldownLabel = inCooldown
                        ? new Date(cooldownIso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                        : null;

                    let statusIcon;
                    let statusText;
                    let statusClass;
                    if (inCooldown) {
                        statusIcon = <Clock size={12} weight="fill" className="text-amber-600" />;
                        statusText = `Cooldown until ${cooldownLabel}`;
                        statusClass = "text-amber-700";
                    } else if (notConfigured) {
                        statusIcon = <XCircle size={12} weight="fill" className="text-rose-600" />;
                        statusText = "Not configured";
                        statusClass = "text-rose-700";
                    } else if (channelId) {
                        statusIcon = <CheckCircle size={12} weight="fill" className="text-emerald-600" />;
                        statusText = `Connected · ${channelId.slice(0, 8)}…`;
                        statusClass = "text-emerald-700";
                    } else {
                        statusIcon = <XCircle size={12} weight="fill" className="text-muted-foreground" />;
                        statusText = "Unknown";
                        statusClass = "text-muted-foreground";
                    }

                    return (
                        <div
                            key={p}
                            data-testid={`buffer-channel-row-${p.toLowerCase()}`}
                            className="flex items-center justify-between text-[13px] font-mono px-2 py-1.5 border-b border-border/50 last:border-0"
                        >
                            <span className="font-semibold">{p}</span>
                            <span className={`inline-flex items-center gap-1.5 ${statusClass}`}>
                                {statusIcon}
                                <span className="text-[11px]">{statusText}</span>
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className={compact ? "mt-2" : "mt-3 pt-3 border-t border-border"}>
                <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm text-[11px] font-mono uppercase tracking-widest"
                    onClick={handleDetect}
                    disabled={detecting}
                    data-testid="buffer-channels-detect"
                >
                    {detecting
                        ? <><ArrowsClockwise size={11} className="mr-1.5 animate-spin" /> Detecting…</>
                        : <><MagnifyingGlass size={11} className="mr-1.5" /> Detect from Buffer</>}
                </Button>
                {missingCount > 0 && !compact && (
                    <p className="text-[10px] font-mono text-muted-foreground mt-2">
                        Missing channels? Click Detect above, OR scroll down to the individual channel-ID inputs and paste the ID from Buffer&apos;s URL when you open a channel.
                    </p>
                )}
            </div>
        </div>
    );
}
